import { modalController, onIonViewDidEnter, onIonViewDidLeave, onIonViewWillEnter, useKeyboard } from "@ionic/vue";
import { computed, nextTick, Ref, ref, watch, reactive } from "vue";
import router from "@/router";
import { useDebounceFn } from "@vueuse/core";

import CreateProduct from "@/components/modal/CreateProduct.vue";
import FilterListModal from "@/components/modal/modalControllerModals/FilterListModal.vue";
import ListSettingsModal from "@/components/modal/modalControllerModals/ListSettingsModal.vue";

import { useListStore } from "@/stores/lists";
import { usePersistentStore } from "@/stores/persistent";
import { useSupplierStore } from "@/stores/suppliers";
import { useUserStore } from "@/stores/user";

import { useLoader } from "@/composables/useLoader";

import handleOfflineAction from "@/functions/offlineMode";
import { interactionFeedbackToast, replaceToast } from "@/functions/baseToast";
import { popAlertConfirm } from "@/functions/alert";
import { isApp } from "@/functions/enviroment";

import { GetListItemsParams, IManualSortingData } from "@/types/list";
import { SORT_METHOD } from "@/types/enum/propEnum";
import { OFFLINE_ACTION } from "@/types/enum/general";
import { ListItem } from "@/types/supplier";
import { PERMISSION_KEY } from "@/types/user";
import { isCatalogCached, isListItemsCached } from "@/functions/checks";
import { ComposerTranslation } from "vue-i18n";

const debounceChangeAmount = ref<boolean>(false)
export let currentItem = { id: '', amount: 0 }
const requestRunning = ref<boolean>(false)
export const multiSelect = ref<boolean>(false)
export const draggingStarted = ref<boolean>(false)
export const selectedItems = reactive<Record<string, boolean>>({});

export function useList(listId: string, locationId: string, supplierId: string, t: ComposerTranslation, catalogRowEl?: Ref) {
    const listStore = useListStore()
    const persistentStore = usePersistentStore()
    const supplierStore = useSupplierStore()
    const userStore = useUserStore()
    const { showLoading, hideLoading } = useLoader()

    const loading = ref<boolean>(false)
    let callIndexCounter = 0
    const callTracker: number[] = []
    let setupRunning = false

    const setup = async (): Promise<void> => {
        if (setupRunning) return
        setupRunning = true
        if (persistentStore.offlineMode) {
            listStore.isLoaded.listItems = true
            supplierStore.isLoaded.catalog = true
            return
        }
        loading.value = true

        supplierStore.isLoaded.listCatalog = isCatalogCached(supplierId, listId);

        await listStore.getAllLocationLists(locationId)

        await Promise.all([
            supplierStore.getLocationSupplier(router.currentRoute.value.params.locationId as string),
        ])

        await listStore.getListItems(new GetListItemsParams({
            locationId,
            listId,
            searchKey: listStore.searchQuery !== '' ? listStore.searchQuery : undefined,
            // category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
            tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
            withQuantity: listStore.listFilter.withQuantity,
            sortDesc: (listStore.listFilter.sortDirection === SORT_METHOD.ALPH_DESC)
        }))

        listStore.isLoaded.listItems = true
        window.dispatchEvent(new Event('list-mounted'))

        await supplierStore.getCatalog({
            locationId: locationId,
            supplierId: supplierId,
            searchKey: supplierStore.searchQuery,
            category: supplierStore.activeCategoriesNames,
            includeList: listId
        })

        supplierStore.isLoaded.catalog = true
        supplierStore.isLoaded.listCatalog = true

        loading.value = false
        setupRunning = false
    }

    const removeTracker = (trackerIndex: number): void => {
        const arrayIndex = callTracker.findIndex(trackerEl => trackerEl === trackerIndex)

        if (arrayIndex === -1) return

        callTracker.splice(arrayIndex, 1)
    }

    const patchItem = async (amount: any, itemId: string, skippedDebounce?: boolean): Promise<void> => {
        const callIndex = Number(callIndexCounter++)
        callTracker.push(callIndex)

        if (persistentStore.offlineMode) {
            debounceChangeAmount.value = false
            const item = listStore.listItems[listId].data?.find((item) => item.id === itemId)
            if (item) item.quantity = amount
            handleOfflineAction(OFFLINE_ACTION.PATCH_ITEM, {
                locationId: locationId,
                listId: listId,
                itemId: itemId,
                amount: amount
            })
            return
        }

        const item = listStore.listItems[listId].data?.find((item) => item.id === itemId)
        if (item) item.quantity = amount

        const response = await listStore.patchListItem(locationId, listId, itemId, amount)

        removeTracker(callIndex)
        if (callTracker.length > 0) return
        if (skippedDebounce) return

        if (!response) listStore.isLoaded.listItems = false

        await listStore.getListItems(new GetListItemsParams({
            locationId,
            listId,
            searchKey: listStore.searchQuery,
            manualSort: listStore.listFilter.sortDirection === SORT_METHOD.MANUAL,
            debounce: true
        }))

        listStore.isLoaded.listItems = true

        debounceChangeAmount.value = false
        if (itemId === currentItem.id) currentItem = {
            id: '',
            amount: 0
        }
    }

    const debouncePatchItem = useDebounceFn(patchItem, 370)

    const disableOnPatch = async (amount: any, itemId: string): Promise<void> => {
        if (!currentItem.id) currentItem = { id: itemId, amount }
        if (currentItem.id && currentItem.id !== itemId) {
            patchItem(currentItem.amount, currentItem.id, true)
            currentItem = { id: itemId, amount }
        }
        currentItem.amount = amount
        debounceChangeAmount.value = true
        await debouncePatchItem(amount, itemId)
    }

    const searchRequest = async (): Promise<void> => {
        if (isApp()) supplierStore.isLoaded.catalog = false
        listStore.isLoaded.listItems = false
        if (persistentStore.offlineMode) {
            setTimeout(() => {
                if (isApp()) supplierStore.isLoaded.catalog = true
                listStore.isLoaded.listItems = true
            }, 300)
            return
        }

        if (requestRunning.value) return

        requestRunning.value = true

        await Promise.all([
            supplierStore.getCatalog({
                locationId,
                supplierId,
            }),
            listStore.getListItems(new GetListItemsParams({
                locationId,
                listId,
                searchKey: listStore.searchQuery !== '' ? listStore.searchQuery : undefined,
                // category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
                tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
                withQuantity: listStore.listFilter.withQuantity,
                sortDesc: (listStore.listFilter.sortDirection === SORT_METHOD.ALPH_DESC)
            }))

        ])

        requestRunning.value = false

        if (isApp()) supplierStore.isLoaded.catalog = true
        listStore.isLoaded.listItems = true
    }

    const addCustomProduct = async (): Promise<void> => {
        const { isOpen } = useKeyboard()
        if (isOpen.value) return
        const modal = await modalController.create({
            component: CreateProduct,
            componentProps: {
                supplierId,
                listId: listStore.singleList(listId)?.id
            }
        })

        modal.onDidDismiss().then(async (value): Promise<void> => {
            if (value.role === 'add') {
                supplierStore.isLoaded.catalog = false
                listStore.isLoaded.listItems = false
                await setup()
            }
        })

        await modal.present()
    }

    const openListFilter = async (): Promise<void> => {
        const modal = await modalController.create({
            component: FilterListModal,
            componentProps: {
                supplierId: supplierId,
                isList: true
            }
        })

        modal.onDidDismiss().then(() => {
            if (listStore.listFilter.sortDirection === SORT_METHOD.MANUAL && listStore.manualSortingsAutoDisplay) multiSelect.value = true
            listStore.handleManualSortingsAutoDisplay(false)
        })


        await modal.present()
    }

    const openListSettings = async (): Promise<void> => {
        const modal = await modalController.create({
            component: ListSettingsModal,
            cssClass: 'list-settings-modal',
            componentProps: {
                supplierId: supplierId,
                isList: true
            }
        })

        await modal.present()
    }

    const deleteHandler = async (itemId: string): Promise<boolean> => {
        listStore.isLoaded.listItems = false
        if (persistentStore.offlineMode) {
            const itemIndex = listStore.listItems[listId].data?.findIndex((item) => item.id === itemId)

            if (itemIndex >= 0) {
                listStore.listItems[listId].data?.splice(itemIndex, 1)
                listStore.listItems[listId].count -= 1
            }

            return await handleOfflineAction(OFFLINE_ACTION.DELETE_ITEM, {
                locationId: locationId,
                listId: listId,
                itemId: itemId,
            })
        }
        const response = await listStore.deleteItem(locationId, listId, itemId)

        if (response) {
            interactionFeedbackToast(
                response,
                {
                    id: 'delete-list',
                    type: 'delete',
                    target: 'product',
                },
                {
                    cssClass: 'toast-offset-80'
                }
            )

            await listStore.getListItems(new GetListItemsParams({
                locationId,
                listId,
                searchKey: supplierStore.searchQuery !== '' ? supplierStore.searchQuery : undefined,
                // category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
                tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
                withQuantity: listStore.listFilter.withQuantity,
                sortDesc: (supplierStore.sortDesc === SORT_METHOD.ALPH_DESC)
            }))

            supplierStore.getCatalog({
                locationId,
                supplierId,
                includeList: listId
            })
            listStore.isLoaded.listItems = true
        }
        return true
    }

    const removeFromList = async (listItem: ListItem): Promise<void> => {
        if (!listItem.id || !userStore.permission[PERMISSION_KEY.LIST_ITEM_DELETE]) return

        await popAlertConfirm({
            message: t('alert.delete.product', { target: listItem.item.name }),
            handler: {
                ok: () => deleteHandler(listItem.id ?? '')
            }
        })
    }

    const closeMultiSelect = (selectedProducts: Record<string, boolean>): void => {
        multiSelect.value = false

        Object.keys(selectedProducts).forEach(id => selectedProducts[id] = false)

        nextTick(() => catalogRowEl?.value.go())
    }

    const removeProducts = async (selectedProducts?: Record<string, boolean>): Promise<void> => {
        if (!selectedProducts) return;

        const selectedIds = Object.keys(selectedProducts).filter(id => selectedProducts[id])

        if (selectedIds.length === 0) return

        await popAlertConfirm({
            message: t('alert.delete.product', 2),
            handler: {
                ok: async () => {
                    await showLoading()

                    // ability to remove multiple items from the list in offline mode (and without an internet-connection)
                    if (persistentStore.offlineMode) {
                        for (const itemId of selectedIds) {
                            const itemIndex = listStore.listItems[listId].data?.findIndex((item) => item.id === itemId)

                            if (itemIndex >= 0) {
                                listStore.listItems[listId].data?.splice(itemIndex, 1)
                                listStore.listItems[listId].count -= 1
                            }

                            await handleOfflineAction(OFFLINE_ACTION.DELETE_ITEM, {
                                locationId: locationId,
                                listId: listId,
                                itemId: itemId,
                            })
                        }

                        setTimeout(() => {
                            replaceToast({
                                message: t('success.deleteMulti', {
                                    target: t('target.product', selectedIds.length),
                                    amount: selectedIds.length
                                }),
                                id: 'delete-multi-item',
                                cssClass: 'toast-offset-80'
                            })
                            listStore.isLoaded.listItems = true
                        }, 100)

                        draggingStarted.value = false;
                        await hideLoading()
                        return;
                    }

                    const response = await listStore.deleteItems(locationId, listId, selectedIds)

                    if (response.length > 0) {
                        closeMultiSelect(selectedProducts)

                        replaceToast({
                            message: t('success.deleteMulti', {
                                target: t('target.product', response.length),
                                amount: response.length
                            }),
                            id: 'delete-multi-item',
                            cssClass: 'toast-offset-80'
                        })
                        listStore.isLoaded.listItems = false

                        await listStore.getListItems(new GetListItemsParams({
                            locationId,
                            listId,
                            searchKey: supplierStore.searchQuery !== '' ? supplierStore.searchQuery : undefined,
                            // category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
                            tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
                            withQuantity: listStore.listFilter.withQuantity,
                            sortField: (supplierStore.sortDesc) ? 'name' : undefined,
                            sortDesc: (supplierStore.sortDesc === SORT_METHOD.ALPH_DESC)
                        }))

                        listStore.isLoaded.listItems = true
                    }
                    await hideLoading()
                    draggingStarted.value = false
                },
                cancel: () => {
                    draggingStarted.value = false
                }
            }
        })
    }

    const manualSort = (selectedProducts: Record<string, boolean>, index: number): void => {
        // console.log('Manual sort', index, selectedProducts)
        const data: IManualSortingData = {
            listId: listId,
            index,
            selectedProducts
        }

        listStore.manualSorting(data)

        for (const product in selectedProducts) {
            selectedProducts[product] = false
        }
    }

    onIonViewWillEnter(() => {
        listStore.searchQuery = ''
    })

    onIonViewDidEnter(() => {
        supplierStore.isLoaded.catalog = isCatalogCached(supplierId)
        listStore.isLoaded.listItems = isListItemsCached(listId)
        setup()
    })

    onIonViewDidLeave(() => {
        // if (!listStore.listItems[listId]) listStore.isLoaded.listItems = false
        // if (!supplierStore.catalog[supplierId]) supplierStore.isLoaded.catalog = false
        listStore.listFilter.tags = {}
        listStore.listFilter.category = {}
        listStore.listFilter.withQuantity = false
    })

    window.addEventListener('reload-list', setup)

    return {
        //functions
        setup,
        loading,
        disableOnPatch,
        searchRequest,
        addCustomProduct,
        openListFilter,
        openListSettings,
        removeFromList,
        removeProducts,
        closeMultiSelect,
        manualSort,

        //global variables
        debounceChangeAmount,
        currentItem,
        requestRunning,
        multiSelect,
        draggingStarted,
        selectedItems
    }
}

export function useListComputed() {
    const listStore = useListStore()

    const listId = router.currentRoute.value.params.listId as string

    const forceReload = ref<boolean>(false)

    const sortedListItems = computed<Array<ListItem>>(() => listStore.filterListItems(listId))
    const isManualSorting = computed<boolean>(() => multiSelect.value && listStore.listFilter.sortDirection === SORT_METHOD.MANUAL)

    watch(isManualSorting, () => {
        if (!isManualSorting.value) return
        forceReload.value = true
        setTimeout(() => {
            forceReload.value = false
        }, 50)
    })

    return {
        sortedListItems,
        isManualSorting,
        forceReload
    }
}

export function useListComputedFooter() {
    const listStore = useListStore()

    const listId = router.currentRoute.value.params.listId as string

    const total = computed<number>(() => {
        if (!listStore.listItems[listId]?.data) return 0

        let price = 0;
        for (const listItem of listStore.listItems[listId].data) {
            if (!listItem.quantity || !listItem.item?.price) continue
            price += (listItem.quantity * listItem.item.price)
        }

        return price
    })

    const positionAmount = computed<number>(() => {
        let amount = 0
        if (listStore.listItems[listId]) {
            for (const listItem of listStore.listItems[listId].data) {
                if (listItem.quantity) amount += 1
            }
        }

        return amount
    })

    const itemsAmount = computed<number>(() => {
        let amount = 0
        if (listStore.listItems[listId]) {
            for (const listItem of listStore.listItems[listId].data) {
                amount += listItem.quantity
            }
        }
        return amount
    })

    return {
        total,
        positionAmount,
        itemsAmount
    }
}
